import { useState } from 'react'; // useEffect removed
import { v4 as uuidv4 } from 'uuid';
import { MemberRole, MemberStatus, permissionOptions } from '@/types/schema'; 
import { useToast } from '@/hooks/use-toast';
import { getAuth } from 'firebase/auth'; 
import { useAuthState } from 'react-firebase-hooks/auth'; 
import { Portfolio as PortfolioZodType, PortfolioData as PortfolioDataZodType } from '@/schemas/portfolio'; // Zod types
import { usePortfolioOperations } from '@/context/schema/usePortfolioOperations';
import { useFirebase } from '@/context/FirebaseProvider'; // To get currentUser for usePortfolioOperations

// Define the interface for portfolio creation input
interface PortfolioCreationInput {
  name: string; // Name is required
}

// Define the return type for createPortfolio
interface PortfolioCreationResult {
  success: boolean;
  portfolioId?: string;
  error?: Error;
}

export const usePortfolioCreation = () => {
  const [isProcessingAction, setIsProcessingAction] = useState(false);
  const { toast } = useToast();
  const auth = getAuth();
  const [firebaseUser, loadingAuth, errorAuth] = useAuthState(auth);
  const { userData: currentUserData } = useFirebase(); // Get currentUser for portfolio operations hook

  // Pass currentUserData (which is UserOutput | null | undefined) to usePortfolioOperations
  const portfolioOps = usePortfolioOperations(currentUserData);


  const createPortfolio = async (formData: PortfolioCreationInput): Promise<PortfolioCreationResult> => {
    console.log('Starting portfolio creation process with data:', formData);
    
    if (!formData.name || formData.name.trim() === '') {
      console.error('Portfolio creation failed: Name is required');
      return { success: false, error: new Error("Portfolio name is required") };
    }
    
    if (!firebaseUser) {
      console.error('Portfolio creation failed: No authenticated user found');
      toast({
        title: "Authentication Error",
        description: "You must be logged in to create a portfolio.",
        variant: "destructive"
      });
      return { success: false, error: new Error("User not authenticated") };
    }
    
    setIsProcessingAction(true);
    
    try {
      // Prepare the data structure expected by usePortfolioOperations().createPortfolio
      // This is Omit<PortfolioDataFromZod, 'id' | 'createdAt' | 'updatedAt' | 'members'>
      // PortfolioDataFromZod is inferred from portfolioSchema (data in Firestore)
      const portfolioDataForOpHook: Omit<PortfolioDataZodType, 'createdAt' | 'updatedAt' | 'members'> = {
        name: formData.name,
        type: 'default', // Default type
        businessRefs: [], // Default empty array
        // Optional fields from portfolioSchema, can be undefined/null or defaulted
        billingInfo: null,
        primaryOwnerRef: firebaseUser.uid, // Primary owner is the current user
        secondaryOwnerRefs: [],
        managerRefs: [],
        owner: firebaseUser.uid, // Legacy owner field
        // Fields like theme, customDomain etc. are not in portfolioSchema, so not included here.
        // lastUpdated is also not part of this input type for create.
      };
      
      const portfolioId = await portfolioOps.createPortfolio(portfolioDataForOpHook, firebaseUser.uid);
      
      console.log('Portfolio successfully created via usePortfolioOperations:', portfolioId);
        
      toast({
        title: "Portfolio created",
        description: `Portfolio "${formData.name}" has been created successfully.`,
      });
      
      return { success: true, portfolioId };
      
    } catch (error) {
      console.error('Error in portfolio creation process via usePortfolioOperations:', error);
      toast({
        title: "Failed to create portfolio",
        description: error instanceof Error ? error.message : "An error occurred.",
        variant: "destructive"
      });
      return { success: false, error: error instanceof Error ? error : new Error(String(error)) };
    } finally {
      setIsProcessingAction(false);
    }
  };
  
  return {
    createPortfolio,
    isProcessingAction,
    currentUser: firebaseUser // Expose firebaseUser if needed by consumers
  };
};

export default usePortfolioCreation;
